import React, { useEffect, useState } from "react";
import ASYNC_TASK_MESSAGES from '../../../other/constants/AsyncTaskMessages.jsx';


const ASYNC_DIALOG_PREF_KEY = 'hideAsyncDialogs';

function getDialogPrefs() {
  try {
    return JSON.parse(localStorage.getItem(ASYNC_DIALOG_PREF_KEY)) || {};
  } catch {
    return {};
  }
}

function setDialogPrefs(prefs) {
  localStorage.setItem(ASYNC_DIALOG_PREF_KEY, JSON.stringify(prefs));
}

export function shouldShowAsyncDialog(type) {
  const prefs = getDialogPrefs();
  return prefs[type] !== true;
}

export function setHideAsyncDialogPref(type, hide) {
  const prefs = getDialogPrefs();
  prefs[type] = !!hide;
  setDialogPrefs(prefs);
}


export const SwivuAsyncMessageModal = ({ record, onClose}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // Get the task message object
  const taskMessage = ASYNC_TASK_MESSAGES[record.record];

  // Extract steps from the message HTML
  const getStepsFromMessage = (message) => {
    if (!message) return [];

    // Remove HTML tags and split by line breaks, then filter for lines that start with "- "
    const cleanMessage = message.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
    const lines = cleanMessage.split(/\r?\n/);

    const steps = lines
      .map(line => line.trim())
      .filter(line => line.startsWith('- '))
      .map(line => line);

    return steps;
  };

  const steps = taskMessage ? getStepsFromMessage(taskMessage.message) : [];
  const title = taskMessage ? taskMessage.title : "Task Started";


  useEffect(() => {
    const prefs = getDialogPrefs();
    if (prefs[record.record] === true) {
      setDontShowAgain(true);
    }
  }, [record.record]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !z-[100000]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[#64646480] animate-in fade-in-0" />

      {/* Dialog Content with scoped reset styles */}
      <div
        className="relative bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-lg mx-4 p-6 animate-in fade-in-0 zoom-in-95 duration-200 border dark:border-gray-700"
        style={{
          boxSizing: "border-box",
          margin: 0,
          lineHeight: "1.5",
          WebkitFontSmoothing: "antialiased"
        }}
      >
        {/* Close Button */}
        <button
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white dark:ring-offset-gray-900 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 dark:focus:ring-slate-300 focus:ring-offset-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit'
          }}
          onClick={() => {
            setHideAsyncDialogPref(record.record, dontShowAgain);
            onClose();
          }}
        >
          <i className="fas fa-times h-4 w-4"></i>
          <span className="sr-only">Close</span>
        </button>

        {/* Header */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xl">
              <i className="fas fa-file-text text-blue-600 dark:text-blue-400"></i>
            </div>
            <div>
              <h2
                className="text-lg font-semibold text-left text-gray-900 dark:text-white"
                style={{ margin: 0, overflowWrap: 'break-word' }}
              >
                {title}
              </h2>
              <p
                className="sm text-slate-500 dark:text-slate-400"
                style={{ margin: 0, overflowWrap: 'break-word' }}
              >
                Processing your request
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">
                Processing...
              </span>
            </div>
            {/* Enhanced Progress Bar */}
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700 shadow-inner">
              {/* Background shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>

              {/* Main progress bar with gradient and glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 dark:from-blue-400 dark:via-blue-500 dark:to-blue-600 rounded-full shadow-lg">
                {/* Animated shimmer overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 animate-pulse rounded-full"></div>

                {/* Moving progress indicator */}
                <div
                  className="absolute top-0 left-0 h-full w-8 bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-full bg-[#60a5fa7d]"
                  style={{
                    animation: 'progress-sweep 2s ease-in-out infinite'
                  }}
                ></div>
              </div>

              {/* Glowing edge effect */}
              <div className="absolute inset-0 rounded-full shadow-inner border border-blue-300/30 dark:border-blue-500/30"></div>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-2 border dark:border-slate-700">
            <h4
              className="font-medium text-gray-900 dark:text-white"
              style={{ margin: 0, overflowWrap: 'break-word' }}
            >
              What happens next?
            </h4>
            <ul
              className="space-y-1 text-slate-500 dark:text-slate-400"
              style={{padding: 0, margin: 0 }}
            >
              {steps.map((step, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span style={{ overflowWrap: "break-word" }}>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => {
                setHideAsyncDialogPref(record.record, dontShowAgain);
                onClose();
              }}
              className="flex-1 inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 dark:focus-visible:ring-slate-300 focus-visible:ring-offset-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 text-slate-900 dark:text-slate-100 h-10 px-4 py-2 border-solid"
              style={{
                cursor: 'pointer',
                font: 'inherit'
              }}
            >
              Continue Working
            </button>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:ring-slate-950 dark:focus:ring-slate-300 bg-white dark:bg-slate-800"
              style={{ font: "inherit" }}
              id="dont-show"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.value)}
            />
            <label
              htmlFor="dont-show"
              className="text-slate-500 dark:text-slate-400"
              style={{ margin: 0 }}
            >
              Don't show this again
            </label>
          </div>
        </div>
      </div>
      {/* Custom CSS for progress animation */}
      <style>{`
        @keyframes progress-sweep {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateX(400%);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SwivuAsyncMessageModal;
